import React, { useEffect, useState } from "react";
import { Eye, EyeOff, LogOut } from "lucide-react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const navigate = useNavigate();

  const [user, setUser] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    gender: "",
    dob: "",
  });

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  // Load user data from Supabase
  useEffect(() => {
    const loadUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data.user) {
        const meta = data.user.user_metadata || {};
        setUser({
          name: meta.full_name || "",
          email: data.user.email || "",
          phone: meta.phone || "",
          address: meta.address || "",
          gender: meta.gender || "",
          dob: meta.dob || "",
        });
      }
    };
    loadUser();
  }, []);

  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  // Save Profile to Supabase user_metadata
  const handleSave = async () => {
    setLoading(true);
    const { error } = await supabase.auth.updateUser({
      data: {
        full_name: user.name,
        phone: user.phone,
        address: user.address,
        gender: user.gender,
        dob: user.dob,
      },
    });
    setLoading(false);

    if (error) {
      alert(error.message);
      return;
    }

    setIsEditing(false);
    alert("Profile Updated Successfully!");
  };

  const handlePasswordChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };

  // Update Password
  const handlePasswordUpdate = async () => {
    if (passwords.newPassword.length < 6) {
      alert("New password must be at least 6 characters");
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    setPasswordLoading(true);
    const { error } = await supabase.auth.updateUser({
      password: passwords.newPassword,
    });
    setPasswordLoading(false);

    if (error) {
      alert(error.message);
      return;
    }

    alert("Password Updated Successfully!");
    setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
  };

  // Logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#faf9f6] via-[#f5f3f0] to-[#f0ede8] py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white/80 backdrop-blur-xl shadow-2xl rounded-3xl p-10 border border-white/40">

        {/* Header */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-10">
          <div className="flex flex-col md:flex-row items-center gap-6">

            {/* Avatar */}
            <div className="w-32 h-32 rounded-full bg-[#e0be9c] flex items-center justify-center text-4xl text-white font-bold shadow-lg">
              {user.name ? user.name.charAt(0).toUpperCase() : "U"}
            </div>

            <div>
              <h2 className="text-3xl font-serif text-[#8b6655]">
                {user.name || "Your Name"}
              </h2>
              <p className="text-[#8b6655]/70">{user.email || "Email"}</p>

              <button
                onClick={() => setIsEditing(!isEditing)}
                className="mt-4 px-6 py-2 rounded-xl bg-[#e0be9c] text-[#8b6655] shadow hover:scale-105 transition"
              >
                {isEditing ? "Cancel" : "Edit Profile"}
              </button>
            </div>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-6 py-3 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 transition"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>

        {/* Profile Form */}
        <div className="grid md:grid-cols-2 gap-6">

          {/* Text Fields */}
          {[
            { label: "Full Name", name: "name" },
            { label: "Email", name: "email" },
            { label: "Phone", name: "phone" },
            { label: "Address", name: "address" },
            { label: "Date of Birth", name: "dob", type: "date" },
          ].map((field) => (
            <div key={field.name}>
              <label className="text-sm text-[#8b6655]/70">{field.label}</label>
              <input
                type={field.type || "text"}
                name={field.name}
                value={user[field.name]}
                onChange={handleChange}
                disabled={!isEditing || field.name === "email"}
                className={`w-full mt-1 px-4 py-3 rounded-xl border 
                  ${isEditing && field.name !== "email" ? "bg-white" : "bg-gray-100"} 
                  border-[#e0be9c]/40 focus:ring-2 focus:ring-[#e0be9c] outline-none transition`}
              />
            </div>
          ))}

          {/* Gender Select */}
          <div>
            <label className="text-sm text-[#8b6655]/70">Gender</label>
            <select
              name="gender"
              value={user.gender}
              onChange={handleChange}
              disabled={!isEditing}
              className={`w-full mt-1 px-4 py-3 rounded-xl border 
                ${isEditing ? "bg-white" : "bg-gray-100"} 
                border-[#e0be9c]/40 focus:ring-2 focus:ring-[#e0be9c] outline-none transition`}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Non-Binary">Non-Binary</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
          </div>

        </div>

        {/* Save Profile Button — only shows when editing */}
        {isEditing && (
          <div className="mt-8 text-center">
            <button
              onClick={handleSave}
              disabled={loading}
              className={`px-8 py-3 rounded-xl bg-gradient-to-r from-[#f0ceaa] to-[#e0be9c] text-[#8b6655] shadow-lg transition ${
                loading ? "opacity-60 cursor-not-allowed" : "hover:scale-105"
              }`}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}

        {/* Change Password Section */}
        <div className="mt-12 border-t pt-8">
          <h3 className="text-xl font-semibold text-[#8b6655] mb-6">
            Change Password
          </h3>

          <div className="grid md:grid-cols-2 gap-6">

            {/* Current Password */}
            <input
              type="password"
              name="currentPassword"
              placeholder="Current Password"
              value={passwords.currentPassword}
              onChange={handlePasswordChange}
              className="w-full px-4 py-3 rounded-xl border border-[#e0be9c]/40 outline-none focus:ring-2 focus:ring-[#e0be9c]"
            />

            {/* New Password */}
            <div className="relative">
              <input
                type={showNewPass ? "text" : "password"}
                name="newPassword"
                placeholder="New Password"
                value={passwords.newPassword}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 rounded-xl border border-[#e0be9c]/40 pr-10 outline-none focus:ring-2 focus:ring-[#e0be9c]"
              />
              <button
                type="button"
                onClick={() => setShowNewPass(!showNewPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8b6655]"
              >
                {showNewPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Confirm New Password */}
            <div className="relative">
              <input
                type={showConfirmPass ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm New Password"
                value={passwords.confirmPassword}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 rounded-xl border border-[#e0be9c]/40 pr-10 outline-none focus:ring-2 focus:ring-[#e0be9c]"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPass(!showConfirmPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8b6655]"
              >
                {showConfirmPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

          </div>

          <div className="mt-6 text-center">
            <button
              onClick={handlePasswordUpdate}
              disabled={passwordLoading}
              className={`px-8 py-3 rounded-xl bg-gradient-to-r from-[#f0ceaa] to-[#e0be9c] text-[#8b6655] shadow-lg transition ${
                passwordLoading ? "opacity-60 cursor-not-allowed" : "hover:scale-105"
              }`}
            >
              {passwordLoading ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Profile;